import { Order } from '@oolio-group/domain';
import React, { createContext, useContext } from 'react';
import useCartEvent from './useCartEvent';
import { useTap2PayAutoLaunch } from './useTap2PayAutoLaunch';
import { useTap2PayEmbeddedInit } from './useTap2PayEmbeddedInit';
import useBehaviorSubjectState from '../rxjs/useSubjectState';
import { sessionSubject } from '../../state/sessionObservable';
import TipSelectionModal from '../../component/Modals/TipSelection/TipSelectionModal';
import BalancePaymentOverlay from '../../component/PaymentStatusView/BalancePaymentOverlay';
import { useBalancePaymentRequest } from './useBalancePaymentRequest';
import EmbeddedTipPrompt from '../../embedded/EmbeddedTipPrompt';

export interface CartProviderValue {
  order: Order;
}
export interface CartProviderProps {
  children: React.ReactNode;
}

export const CartContext = createContext<CartProviderValue>({
  order: {} as Order,
});

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const cart = useCartEvent();
  const { value: session } = useBehaviorSubjectState(sessionSubject);
  useTap2PayEmbeddedInit(session);
  const { tipModal } = useTap2PayAutoLaunch(cart.order, session);
  const { balancePayment, resetBalancePayment } =
    useBalancePaymentRequest(session);
  return (
    <CartContext.Provider value={cart}>
      {children}
      <BalancePaymentOverlay
        state={balancePayment}
        onDismiss={resetBalancePayment}
      />
      {tipModal.visible && (
        <TipSelectionModal
          amount={tipModal.amount}
          tipOptions={tipModal.tipOptions}
          onContinue={tipModal.onContinue}
          onSkip={tipModal.onSkip}
          onClose={tipModal.onClose}
        />
      )}
      <EmbeddedTipPrompt />
    </CartContext.Provider>
  );
};

export const useCart = (): CartProviderValue => {
  return useContext(CartContext);
};
